import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { entityService } from '../../../services/entityService';
import { useSpaceStore } from '../../../stores/spaceStore';
import { createFileLogger } from '../../../utils/logger';
import type { 
  Task, 
  Project, 
  Area, 
  TasksView, 
  TaskFilters, 
  TaskSort, 
  TasksAppState 
} from '../types';

const log = createFileLogger('TASKS_APP');

// Store instances map
const tasksAppStores = new Map<string, ReturnType<typeof createTasksAppStore>>();

interface TasksAppStore extends TasksAppState {
  windowId: string;
  spaceId: string;
  isInitialized: boolean;
  
  // Initialization
  initialize: (windowId: string, spaceId: string) => Promise<void>;
  cleanup: () => void;
  
  // Navigation
  navigateToView: (view: TasksView) => void;
  navigateToProject: (projectId: string) => void;
  navigateToArea: (areaId: string) => void;
  navigateToTag: (tag: string) => void;
  navigateToSearch: () => void;
  
  // Task operations
  createTask: (taskData: Partial<Task['skeleton']>) => Promise<Task | null>;
  updateTask: (taskId: string, updates: Partial<Task['skeleton']>) => Promise<void>;
  deleteTask: (taskId: string) => Promise<void>;
  completeTask: (taskId: string) => Promise<void>;
  uncompleteTask: (taskId: string) => Promise<void>;
  moveTaskToToday: (taskId: string) => Promise<void>;
  removeTaskFromToday: (taskId: string) => Promise<void>;
  
  // Draft task operations
  createDraftTask: (taskData: Partial<Task['skeleton']>) => void;
  updateDraftTask: (updates: Partial<Task['skeleton']>) => void;
  saveDraftTask: (title: string, notes?: string) => Promise<Task | null>;
  cancelDraftTask: () => void;
  
  // Project operations
  createProject: (projectData: Partial<Project['skeleton']>) => Promise<Project | null>;
  updateProject: (projectId: string, updates: Partial<Project['skeleton']>) => Promise<void>;
  deleteProject: (projectId: string) => Promise<void>;
  
  // Area operations
  createArea: (areaData: Partial<Area['skeleton']>) => Promise<Area | null>;
  updateArea: (areaId: string, updates: Partial<Area['skeleton']>) => Promise<void>;
  deleteArea: (areaId: string) => Promise<void>;
  
  // Data loading
  loadTasks: () => Promise<void>;
  loadProjects: () => Promise<void>;
  loadAreas: () => Promise<void>;
  loadAllData: () => Promise<void>;
  
  // Filtering and searching
  setSearchQuery: (query: string) => void;
  setFilters: (filters: Partial<TaskFilters>) => void;
  setSort: (sort: TaskSort) => void;
  clearFilters: () => void;
  
  // Selection
  selectTask: (taskId: string) => void;
  deselectTask: (taskId: string) => void;
  selectAllTasks: () => void;
  clearSelection: () => void;
  setSelectedTaskId: (taskId: string | null) => void;
  
  // Quick add
  setQuickAddText: (text: string) => void;
  toggleQuickAdd: () => void;
  submitQuickAdd: () => Promise<void>;
  
  // Editing
  setEditingTaskId: (taskId: string | null) => void;
  setExpandedTaskId: (taskId: string | null) => void;
  createAndEditTask: () => Promise<void>;
  
  // Display mode
  setDisplayMode: (mode: 'list' | 'board') => void;
  
  // Computed getters
  getFilteredTasks: () => Task[];
  getTodayTasks: () => Task[];
  getTodayCompletedTasks: () => Task[];
  getUpcomingTasks: () => Task[];
  getInboxTasks: () => Task[];
  getProjectTasks: (projectId: string) => Task[];
  getAreaTasks: (areaId: string) => Task[];
  getTasksByTag: (tag: string) => Task[];
  getCompletedTasks: () => Task[];

  // Demo mode
  isDemo: boolean;
  loadDemoData: (areas: Area[], projects: Project[], tasks: Task[]) => void;
}

const createTasksAppStore = (windowId: string, spaceId: string) => {
  let unsubscribeEntityStore: (() => void) | null = null;
  
  return create<TasksAppStore>()(
    subscribeWithSelector(
      immer((set, get) => ({
        windowId,
        spaceId,
        isInitialized: false,
        
        // Initial state
        currentView: 'inbox' as TasksView,
        selectedProjectId: undefined,
        selectedAreaId: undefined,
        selectedTag: undefined,
        
        tasks: [],
        projects: [],
        areas: [],
        
        isLoading: false,
        error: null,
        searchQuery: '',
        filters: {},
        sort: { field: 'order', direction: 'asc' } as TaskSort,
        displayMode: 'list' as 'list' | 'board',
        
        selectedTaskIds: [],
        selectedTaskId: null,
        quickAddText: '',
        showQuickAdd: false,
        editingTaskId: null,
        expandedTaskId: null,
        draftTask: null,
        isDemo: false,
        
        // Initialization
        initialize: async (windowId: string, spaceId: string) => {
          if (get().isInitialized) return;

          try {
            set((state) => {
              state.windowId = windowId;
              state.spaceId = spaceId;
              state.isLoading = true;
              state.error = null;
            });
            
            log.info('[TasksApp] Initializing store', { windowId, spaceId });
            
            // Read URL parameters for initial state
            const params = new URLSearchParams(window.location.search);
            const urlView = params.get('view') as TasksView || 'inbox';
            const urlProject = params.get('project');
            const urlArea = params.get('area');
            const urlTag = params.get('tag');
            const urlSearch = params.get('search');
            
            // Subscribe to entity store BEFORE loading data so that any entity-store
            // update that races with loadAllData (e.g. CRDT sync completing) is caught.
            const useEntityStore = (await import('../../../stores/entityStore')).default;
            
            // Clean up any existing subscription first
            if (unsubscribeEntityStore) {
              unsubscribeEntityStore();
              unsubscribeEntityStore = null;
            }
            
            unsubscribeEntityStore = useEntityStore.subscribe((state: any, prevState: any) => {
              // Only process if entities actually changed
              if (state.entities === prevState.entities) return;
              
              const currentTasks = get().tasks;
              const currentProjects = get().projects;
              const currentAreas = get().areas;
              
              // Tasks, Projects, and Areas are user-global (not space-scoped)
              const updatedTasks = state.entities.filter(
                (e: any) => e.entityType === 'Task' && !e.trashed
              ) as Task[];
              
              const updatedProjects = state.entities.filter(
                (e: any) => e.entityType === 'Project' && !e.trashed
              ) as Project[];
              
              const updatedAreas = state.entities.filter(
                (e: any) => e.entityType === 'Area' && !e.trashed
              ) as Area[];

              // Detect skeleton-less entities from CRDT broadcast (skeleton stripped for perf).
              // Trigger a full REST reload so the heavy data comes in via loadAllData().
              const prevSkeletonlessTaskCount = prevState.entities.filter(
                (e: any) => e.entityType === 'Task' && !e.trashed && !e.skeleton
              ).length;
              const skeletonlessTaskCount = state.entities.filter(
                (e: any) => e.entityType === 'Task' && !e.trashed && !e.skeleton
              ).length;
              const prevSkeletonlessProjCount = prevState.entities.filter(
                (e: any) => e.entityType === 'Project' && !e.trashed && !e.skeleton
              ).length;
              const skeletonlessProjCount = state.entities.filter(
                (e: any) => e.entityType === 'Project' && !e.trashed && !e.skeleton
              ).length;
              const prevSkeletonlessAreaCount = prevState.entities.filter(
                (e: any) => e.entityType === 'Area' && !e.trashed && !e.skeleton
              ).length;
              const skeletonlessAreaCount = state.entities.filter(
                (e: any) => e.entityType === 'Area' && !e.trashed && !e.skeleton
              ).length;

              if (
                skeletonlessTaskCount > prevSkeletonlessTaskCount ||
                skeletonlessProjCount > prevSkeletonlessProjCount ||
                skeletonlessAreaCount > prevSkeletonlessAreaCount
              ) {
                log.info('[TasksApp] Skeleton-less entities arrived via CRDT — reloading via REST');
                get().loadAllData();
                return;
              }
              
              // Check if data actually changed (all fields that affect view membership or display)
              const tasksChanged = updatedTasks.length !== currentTasks.length ||
                updatedTasks.some((task: Task) => {
                  const currentTask = currentTasks.find(t => t._id === task._id);
                  if (!currentTask) return true;
                  
                  return currentTask.name !== task.name ||
                    currentTask.skeleton?.status !== task.skeleton?.status ||
                    currentTask.skeleton?.order !== task.skeleton?.order ||
                    currentTask.skeleton?.completed !== task.skeleton?.completed ||
                    currentTask.skeleton?.dueDate !== task.skeleton?.dueDate ||
                    currentTask.skeleton?.projectId !== task.skeleton?.projectId ||
                    currentTask.skeleton?.areaId !== task.skeleton?.areaId ||
                    currentTask.skeleton?.priority !== task.skeleton?.priority ||
                    currentTask.skeleton?.isInToday !== task.skeleton?.isInToday ||
                    currentTask.skeleton?.notes !== task.skeleton?.notes ||
                    JSON.stringify(currentTask.skeleton?.tags) !== JSON.stringify(task.skeleton?.tags);
                });
              
              const projectsChanged = updatedProjects.length !== currentProjects.length ||
                updatedProjects.some((project: Project) => {
                  const currentProject = currentProjects.find(p => p._id === project._id);
                  return !currentProject || 
                    currentProject.name !== project.name ||
                    currentProject.updated_at !== project.updated_at;
                });
              
              const areasChanged = updatedAreas.length !== currentAreas.length ||
                updatedAreas.some((area: Area) => {
                  const currentArea = currentAreas.find(a => a._id === area._id);
                  return !currentArea || 
                    currentArea.name !== area.name ||
                    currentArea.updated_at !== area.updated_at;
                });
              
              // Update state if anything changed
              if (tasksChanged || projectsChanged || areasChanged) {
                
                set((state) => {
                  if (tasksChanged) {
                    const mappedTasks = updatedTasks
                      .filter(task => task.skeleton)
                      .map(task => ({
                      ...task,
                      name: task.name || (task.skeleton as any).title || '',
                      skeleton: {
                        ...task.skeleton,
                        '@type': 'Task' as const,
                        notes: task.skeleton.notes || '',
                        status: task.skeleton.status || 'todo',
                        completed: task.skeleton.completed || false,
                        tags: task.skeleton.tags || [],
                        priority: task.skeleton.priority || 'medium',
                        isInToday: task.skeleton.isInToday || false,
                        order: task.skeleton.order !== undefined ? task.skeleton.order : Date.now()
                      }
                    }));
                    
                    // DON'T sort here - let the getter functions handle sorting
                    // This preserves the order from entity store
                    state.tasks = mappedTasks;
                  }
                  
                  if (projectsChanged) {
                    state.projects = updatedProjects.map(project => ({
                      ...project,
                      name: project.name || (project.skeleton as any).title || 'Untitled Project',
                      skeleton: {
                        ...project.skeleton,
                        '@type': 'Project' as const,
                        notes: project.skeleton.notes || '',
                        completed: project.skeleton.completed || false,
                        order: project.skeleton.order || Date.now()
                      }
                    }));
                  }
                  
                  if (areasChanged) {
                    state.areas = updatedAreas.map(area => ({
                      ...area,
                      name: area.name || (area.skeleton as any).title || 'Untitled Area',
                      skeleton: {
                        ...area.skeleton,
                        '@type': 'Area' as const,
                        notes: area.skeleton.notes || '',
                        order: area.skeleton.order || Date.now()
                      }
                    }));
                  }
                });
              }
            });

            // Load all data after subscription is wired
            await get().loadAllData();
            
            // Restore navigation state: prefer persisted applicationState, fall back to URL params.
            // applicationState.tasks is written by setupTasksAppSync on every navigation change
            // and is CRDT-synced, so it survives page reloads.
            const persistedNav = useSpaceStore.getState().windows[windowId]?.applicationState?.tasks as {
              currentView?: TasksView;
              selectedProjectId?: string;
              selectedAreaId?: string;
              selectedTag?: string;
              searchQuery?: string;
            } | undefined;

            if (persistedNav?.selectedProjectId) {
              get().navigateToProject(persistedNav.selectedProjectId);
            } else if (persistedNav?.selectedAreaId) {
              get().navigateToArea(persistedNav.selectedAreaId);
            } else if (persistedNav?.selectedTag) {
              get().navigateToTag(persistedNav.selectedTag);
            } else if (persistedNav?.currentView === 'search' || persistedNav?.searchQuery) {
              if (persistedNav.searchQuery) get().setSearchQuery(persistedNav.searchQuery);
              get().navigateToSearch();
            } else if (persistedNav?.currentView && persistedNav.currentView !== 'inbox') {
              get().navigateToView(persistedNav.currentView);
            } else if (urlProject) {
              // URL param fallback (e.g. deep link from useWindowEntitySync)
              get().navigateToProject(urlProject);
            } else if (urlArea) {
              get().navigateToArea(urlArea);
            } else if (urlTag) {
              get().navigateToTag(urlTag);
            } else if (urlView === 'search' || urlSearch) {
              if (urlSearch) get().setSearchQuery(urlSearch);
              get().navigateToSearch();
            } else {
              get().navigateToView(urlView);
            }
            
            set((state) => {
              state.isLoading = false;
              state.isInitialized = true;
            });
            
            log.info('[TasksApp] Store initialized successfully');
            
          } catch (error) {
            log.error('[TasksApp] Failed to initialize store:', error);
            set((state) => {
              state.error = error instanceof Error ? error.message : 'Failed to initialize';
              state.isLoading = false;
            });
          }
        },
        
        cleanup: () => {
          if (unsubscribeEntityStore) {
            unsubscribeEntityStore();
            unsubscribeEntityStore = null;
          }
          
          set((state) => {
            state.tasks = [];
            state.projects = [];
            state.areas = [];
            state.selectedTaskIds = [];
            state.selectedTaskId = null;
            state.searchQuery = '';
            state.quickAddText = '';
            state.error = null;
            state.isLoading = false;
            state.isInitialized = false;
          });
        },
        
        // Navigation methods
        navigateToView: (view: TasksView) => {
          set((state) => {
            state.currentView = view;
            state.selectedProjectId = undefined;
            state.selectedAreaId = undefined;
            state.selectedTag = undefined;
          });
          
          // Update URL
          const params = new URLSearchParams();
          if (view !== 'inbox') params.set('view', view);
          const newUrl = `${window.location.pathname}${params.toString() ? '?' + params : ''}`;
          window.history.replaceState(null, '', newUrl);
        },
        
        navigateToProject: (projectId: string) => {
          set((state) => {
            state.currentView = 'project';
            state.selectedProjectId = projectId;
            state.selectedAreaId = undefined;
            state.selectedTag = undefined;
          });
          
          // Update URL
          const params = new URLSearchParams();
          params.set('view', 'project');
          params.set('project', projectId);
          const newUrl = `${window.location.pathname}?${params}`;
          window.history.replaceState(null, '', newUrl);
        },
        
        navigateToArea: (areaId: string) => {
          set((state) => {
            state.currentView = 'area';
            state.selectedAreaId = areaId;
            state.selectedProjectId = undefined;
            state.selectedTag = undefined;
          });
          
          // Update URL
          const params = new URLSearchParams();
          params.set('view', 'area');
          params.set('area', areaId);
          const newUrl = `${window.location.pathname}?${params}`;
          window.history.replaceState(null, '', newUrl);
        },
        
        navigateToTag: (tag: string) => {
          set((state) => {
            state.currentView = 'tags';
            state.selectedTag = tag;
            state.selectedProjectId = undefined;
            state.selectedAreaId = undefined;
          });
          
          // Update URL
          const params = new URLSearchParams();
          params.set('view', 'tags');
          params.set('tag', tag);
          const newUrl = `${window.location.pathname}?${params}`;
          window.history.replaceState(null, '', newUrl);
        },
        
        navigateToSearch: () => {
          set((state) => {
            state.currentView = 'search';
            state.selectedProjectId = undefined;
            state.selectedAreaId = undefined;
            state.selectedTag = undefined;
          });
          
          // Update URL
          const params = new URLSearchParams();
          params.set('view', 'search');
          const newUrl = `${window.location.pathname}?${params}`;
          window.history.replaceState(null, '', newUrl);
        },
        
        // Task operations
        createTask: async (taskData) => {
          try {
            const { spaceId } = get();
            
            // Create task entity
            // Ensure title is not empty (validation requirement)
            const taskTitle = (taskData.title && taskData.title.trim()) || 'Untitled Task';
            const status = taskData.status || 'todo';
            
            // ✅ Use entityStore.actions.createEntity for proper store integration
            // This ensures Tasks are in entityStore for project progress calculations
            const useEntityStore = (await import('../../../stores/entityStore')).default;
            const entityStore = useEntityStore.getState();
            
            const task = await entityStore.actions.createEntity({
              entityType: 'Task',
              spaceId,
              name: taskTitle,
              hidden: true,
              skeleton: {
                '@type': 'Task',
                notes: taskData.notes || '',
                status: status,
                completed: status === 'done',
                dueDate: taskData.dueDate,
                tags: taskData.tags || [],
                projectId: taskData.projectId,
                areaId: taskData.areaId,
                priority: taskData.priority || 'medium',
                isInToday: taskData.isInToday || false,
                order: taskData.order || Date.now() // Use timestamp for default ordering
              }
            } as any) as Task;
            
            if (task) {
              // ✅ DON'T manually push - entity store subscription will handle it
              log.info('[TasksApp] Task created:', task.name);
              return task;
            }
            return null;
          } catch (error) {
            log.error('[TasksApp] Failed to create task:', error);
            set((state) => {
              state.error = error instanceof Error ? error.message : 'Failed to create task';
            });
            return null;
          }
        },
        
        updateTask: async (taskId: string, updates) => {
          try {
            // ✅ Use entityStore.actions for proper store integration
            const useEntityStore = (await import('../../../stores/entityStore')).default;
            const entityStore = useEntityStore.getState();
            
            const updatePayload: any = {};
            
            // If updating title, update the root name field
            if (updates.title) {
              updatePayload.name = updates.title;
            }
            
            // Add skeleton updates (excluding title since that's in name)
            const { title, ...skeletonUpdates } = updates;
            if (Object.keys(skeletonUpdates).length > 0) {
              updatePayload.skeleton = skeletonUpdates;
            }
            
            await entityStore.actions.updateEntity(taskId, updatePayload);
            
            // Update TasksApp local store
            set((state) => {
              const taskIndex = state.tasks.findIndex(t => t._id === taskId);
              if (taskIndex !== -1) {
                state.tasks[taskIndex].skeleton = { ...state.tasks[taskIndex].skeleton, ...skeletonUpdates };
                if (updates.title) {
                  state.tasks[taskIndex].name = updates.title;
                }
                state.tasks[taskIndex].updated_at = new Date().toISOString();
              }
            });
            
            log.info('[TasksApp] Task updated:', taskId);
          } catch (error) {
            log.error('[TasksApp] Failed to update task:', error);
            set((state) => {
              state.error = error instanceof Error ? error.message : 'Failed to update task';
            });
          }
        },
        
        deleteTask: async (taskId: string) => {
          try {
            // ✅ Use entityStore.actions for proper store integration
            const useEntityStore = (await import('../../../stores/entityStore')).default;
            const entityStore = useEntityStore.getState();
            
            await entityStore.actions.softDeleteEntity(taskId);

            // Update TasksApp local store
            set((state) => {
              state.tasks = state.tasks.filter(t => t._id !== taskId);
              state.selectedTaskIds = state.selectedTaskIds.filter(id => id !== taskId);
            });

            log.info('[TasksApp] Task deleted:', taskId);
          } catch (error) {
            log.error('[TasksApp] Failed to delete task:', error);
            set((state) => {
              state.error = error instanceof Error ? error.message : 'Failed to delete task';
            });
          }
        },
        
        completeTask: async (taskId: string) => {
          await get().updateTask(taskId, {
            status: 'done',
            completed: true,
            completedAt: new Date().toISOString()
          });
        },
        
        uncompleteTask: async (taskId: string) => {
          await get().updateTask(taskId, {
            status: 'todo',
            completed: false,
            completedAt: undefined
          });
        },
        
        moveTaskToToday: async (taskId: string) => {
          await get().updateTask(taskId, {
            isInToday: true,
            addedToTodayAt: new Date().toISOString()
          });
        },
        
        removeTaskFromToday: async (taskId: string) => {
          await get().updateTask(taskId, {
            isInToday: false,
            addedToTodayAt: undefined
          });
        },
        
        // Draft task operations (create in local state first, persist later)
        createDraftTask: (taskData) => {
          const { spaceId } = get();
          const draftId = `draft_${Date.now()}`;
          
          // Create draft task in local state only
          const draftTask: Task = {
            _id: draftId,
            id: draftId,
            entityType: 'Task',
            type: 'task',  // Required by BaseEntityType
            name: '',  // Start with empty name
            spaceId,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            skeleton: {
              '@type': 'Task',
              notes: taskData.notes || '',
              status: taskData.status || 'todo',
              completed: false,
              tags: taskData.tags || [],
              projectId: taskData.projectId,
              areaId: taskData.areaId,
              priority: taskData.priority || 'medium',
              isInToday: taskData.isInToday || false,
              order: Date.now()
            }
          } as Task;
          
          set((state) => {
            state.draftTask = draftTask;
            state.editingTaskId = draftId;
            state.expandedTaskId = draftId;
          });
          
          log.info('[TasksApp] Draft task created:', { draftId });
        },
        
        saveDraftTask: async (title: string, notes?: string) => {
          const { draftTask } = get();
          if (!draftTask) return null;
          
          // Use "Untitled Task" if title is empty
          const finalTitle = title.trim() || 'Untitled Task';
          
          log.info('[TasksApp] Saving draft task:', { title: finalTitle });
          
          // Create actual task on backend with all current draft values
          const taskData: Partial<Task['skeleton']> = {
            title: finalTitle,
            notes: notes !== undefined ? notes : draftTask.skeleton.notes,
            status: draftTask.skeleton.status,
            tags: draftTask.skeleton.tags,
            projectId: draftTask.skeleton.projectId,
            areaId: draftTask.skeleton.areaId,
            priority: draftTask.skeleton.priority,
            isInToday: draftTask.skeleton.isInToday,
            dueDate: draftTask.skeleton.dueDate,
            order: draftTask.skeleton.order
          };
          
          const task = await get().createTask(taskData);
          
          // Clear draft task
          set((state) => {
            state.draftTask = null;
            state.editingTaskId = null;
            state.expandedTaskId = null;
            state.selectedTaskId = null;
          });
          
          log.info('[TasksApp] Draft task saved:', { taskId: task?._id });
          return task;
        },
        
        updateDraftTask: (updates) => {
          set((state) => {
            if (!state.draftTask) return;
            
            // Update draft task skeleton
            state.draftTask.skeleton = {
              ...state.draftTask.skeleton,
              ...updates
            };
          });
          
          log.info('[TasksApp] Draft task updated:', { updates });
        },
        
        cancelDraftTask: () => {
          set((state) => {
            state.draftTask = null;
            state.editingTaskId = null;
            state.expandedTaskId = null;
          });
          
          log.info('[TasksApp] Draft task cancelled');
        },
        
        // Project operations
        createProject: async (projectData) => {
          try {
            const { spaceId } = get();

            // ✅ Use entityStore.actions.createEntity for proper store integration
            const useEntityStore = (await import('../../../stores/entityStore')).default;
            const entityStore = useEntityStore.getState();
            
            const project = await entityStore.actions.createEntity({
              entityType: 'Project',
              spaceId,
              name: projectData.title || 'Untitled Project',
              icon: 'Circle',
              skeleton: {
                '@type': 'Project',
                notes: projectData.notes || '',
                completed: false,
                order: projectData.order || Date.now(),
                areaId: projectData.areaId,
                tags: projectData.tags,
                color: projectData.color,
                dueDate: projectData.dueDate
              }
            } as any) as Project;
            
            if (project) {
              // ✅ DON'T manually push - entity store subscription will handle it
              log.info('[TasksApp] Project created:', project.name);
              return project;
            }
            return null;
          } catch (error) {
            log.error('[TasksApp] Failed to create project:', error);
            set((state) => {
              state.error = error instanceof Error ? error.message : 'Failed to create project';
            });
            return null;
          }
        },
        
        updateProject: async (projectId: string, updates) => {
          try {
            const useEntityStore = (await import('../../../stores/entityStore')).default;
            const entityStore = useEntityStore.getState();

            const updatePayload: any = {};
            
            if (updates.title) {
              updatePayload.name = updates.title;
            }
            
            if (updates.tags !== undefined) {
              updatePayload.tags = updates.tags;
            }
            
            const { title, tags, ...skeletonUpdates } = updates;
            if (Object.keys(skeletonUpdates).length > 0) {
              updatePayload.skeleton = skeletonUpdates;
            }
            
            await entityStore.actions.updateEntity(projectId, updatePayload);
            
            set((state) => {
              const projectIndex = state.projects.findIndex(p => p._id === projectId);
              if (projectIndex !== -1) {
                state.projects[projectIndex].skeleton = { ...state.projects[projectIndex].skeleton, ...skeletonUpdates };
                if (updates.title) {
                  state.projects[projectIndex].name = updates.title;
                }
                if (updates.tags !== undefined) {
                  state.projects[projectIndex].tags = updates.tags;
                }
                state.projects[projectIndex].updated_at = new Date().toISOString();
              }
            });
            
            log.info('[TasksApp] Project updated:', projectId);
          } catch (error) {
            log.error('[TasksApp] Failed to update project:', error);
            set((state) => {
              state.error = error instanceof Error ? error.message : 'Failed to update project';
            });
          }
        },
        
        deleteProject: async (projectId: string) => {
          try {
            const useEntityStore = (await import('../../../stores/entityStore')).default;
            const entityStore = useEntityStore.getState();

            await entityStore.actions.softDeleteEntity(projectId);
            
            set((state) => {
              state.projects = state.projects.filter(p => p._id !== projectId);
              state.tasks.forEach(task => {
                if (task.skeleton.projectId === projectId) {
                  task.skeleton.projectId = undefined;
                }
              });
            });
            
            log.info('[TasksApp] Project deleted:', projectId);
          } catch (error) {
            log.error('[TasksApp] Failed to delete project:', error);
            set((state) => {
              state.error = error instanceof Error ? error.message : 'Failed to delete project';
            });
          }
        },
        
        // Area operations  
        createArea: async (areaData) => {
          try {
            const { spaceId } = get();

            const useEntityStore = (await import('../../../stores/entityStore')).default;
            const entityStore = useEntityStore.getState();

            const area = await entityStore.actions.createEntity({
              entityType: 'Area',
              spaceId,
              name: areaData.title || 'Untitled Area',
              icon: 'Tag',
              skeleton: {
                '@type': 'Area',
                notes: areaData.notes || '',
                order: areaData.order || Date.now(),
                tags: areaData.tags,
                color: areaData.color
              }
            } as any) as Area;
            
            if (area) {
              log.info('[TasksApp] Area created:', area.name);
              return area;
            }
            return null;
          } catch (error) {
            log.error('[TasksApp] Failed to create area:', error);
            set((state) => {
              state.error = error instanceof Error ? error.message : 'Failed to create area';
            });
            return null;
          }
        },
        
        updateArea: async (areaId: string, updates) => {
          try {
            const useEntityStore = (await import('../../../stores/entityStore')).default;
            const entityStore = useEntityStore.getState();

            const updatePayload: any = {};
            
            if (updates.title) {
              updatePayload.name = updates.title;
            }
            
            if (updates.tags !== undefined) {
              updatePayload.tags = updates.tags;
            }
            
            const { title, tags, ...skeletonUpdates } = updates;
            if (Object.keys(skeletonUpdates).length > 0) {
              updatePayload.skeleton = skeletonUpdates;
            }
            
            await entityStore.actions.updateEntity(areaId, updatePayload);
            
            set((state) => {
              const areaIndex = state.areas.findIndex(a => a._id === areaId);
              if (areaIndex !== -1) {
                state.areas[areaIndex].skeleton = { ...state.areas[areaIndex].skeleton, ...skeletonUpdates };
                if (updates.title) {
                  state.areas[areaIndex].name = updates.title;
                }
                if (updates.tags !== undefined) {
                  state.areas[areaIndex].tags = updates.tags;
                }
                state.areas[areaIndex].updated_at = new Date().toISOString();
              }
            });
            
            log.info('[TasksApp] Area updated:', areaId);
          } catch (error) {
            log.error('[TasksApp] Failed to update area:', error);
            set((state) => {
              state.error = error instanceof Error ? error.message : 'Failed to update area';
            });
          }
        },
        
        deleteArea: async (areaId: string) => {
          try {
            const useEntityStore = (await import('../../../stores/entityStore')).default;
            const entityStore = useEntityStore.getState();

            await entityStore.actions.softDeleteEntity(areaId);
            
            set((state) => {
              state.areas = state.areas.filter(a => a._id !== areaId);
              state.tasks.forEach(task => {
                if (task.skeleton.areaId === areaId) {
                  task.skeleton.areaId = undefined;
                }
              });
              state.projects.forEach(project => {
                if (project.skeleton.areaId === areaId) {
                  project.skeleton.areaId = undefined;
                }
              });
            });
            
            log.info('[TasksApp] Area deleted:', areaId);
          } catch (error) {
            log.error('[TasksApp] Failed to delete area:', error);
            set((state) => {
              state.error = error instanceof Error ? error.message : 'Failed to delete area';
            });
          }
        },
        
        // Data loading
        loadTasks: async () => {
          try {
            const rawTasks = await entityService.getEntitiesByType({
              entityType: 'Task'
            }) as Task[];

            // Filter out trashed tasks and normalize to ensure all properties have defaults
            const tasks = rawTasks
              .filter(task => !task.trashed && task.skeleton)
              .map(task => ({
                ...task,
                // Ensure name is set (fallback for old data that might have skeleton.title)
                name: task.name || (task.skeleton as any).title || '',
                skeleton: {
                  ...task.skeleton,
                  '@type': 'Task' as const,
                  notes: task.skeleton.notes || '',
                  status: task.skeleton.status || 'todo',
                  completed: task.skeleton.completed || false,
                  tags: task.skeleton.tags || [],
                  priority: task.skeleton.priority || 'medium',
                  isInToday: task.skeleton.isInToday || false,
                  order: task.skeleton.order ?? Date.now()
                }
              }));
            
            // ✅ FIX: Only normalize tasks with timestamp-based orders (> 1000000000000)
            // Preserve fractional orders like 1.5
            const tasksNeedingNormalization = tasks.filter(t => 
              t.skeleton.order === undefined || t.skeleton.order > 1000000000000
            );
            
            if (tasksNeedingNormalization.length > 0) {
              // Sort by created_at to assign sequential orders
              const sortedForNormalization = tasksNeedingNormalization
                .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
              
              // Find the max existing order
              const maxExistingOrder = Math.max(
                ...tasks.filter(t => t.skeleton.order < 1000000000000).map(t => t.skeleton.order),
                -1
              );
              
              // Assign sequential orders starting after the max
              sortedForNormalization.forEach((task, index) => {
                task.skeleton.order = maxExistingOrder + 1 + index;
              });
            }

            log.info('[TasksApp] Loaded tasks:', tasks.length);

            set((state) => {
              state.tasks = tasks;
            });
          } catch (error) {
            log.error('[TasksApp] Failed to load tasks:', error);
            throw error;
          }
        },
        
        loadProjects: async () => {
          try {
            const rawProjects = await entityService.getEntitiesByType({
              entityType: 'Project'
            }) as Project[];

            // Filter out trashed projects and normalize to ensure all properties have defaults
            const projects = rawProjects
              .filter(project => !project.trashed)
              .map(project => ({
                ...project,
                // Ensure name is set (fallback for old data that might have skeleton.title)
                name: project.name || (project.skeleton as any).title || 'Untitled Project',
                skeleton: {
                  ...project.skeleton,
                  '@type': 'Project' as const,
                  notes: project.skeleton.notes || '',
                  completed: project.skeleton.completed || false,
                  order: project.skeleton.order || Date.now()
                }
              }));

            set((state) => {
              state.projects = projects;
            });

            log.info('[TasksApp] Loaded projects:', projects.length);
          } catch (error) {
            log.error('[TasksApp] Failed to load projects:', error);
            throw error;
          }
        },
        
        loadAreas: async () => {
          try {
            const rawAreas = await entityService.getEntitiesByType({
              entityType: 'Area'
            }) as Area[];

            // Filter out trashed areas and normalize to ensure all properties have defaults
            const areas = rawAreas
              .filter(area => !area.trashed)
              .map(area => ({
                ...area,
                // Ensure name is set (fallback for old data that might have skeleton.title)
                name: area.name || (area.skeleton as any).title || 'Untitled Area',
                skeleton: {
                  ...area.skeleton,
                  '@type': 'Area' as const,
                  notes: area.skeleton.notes || '',
                  order: area.skeleton.order || Date.now()
                }
              }));

            set((state) => {
              state.areas = areas;
            });

            log.info('[TasksApp] Loaded areas:', areas.length);
          } catch (error) {
            log.error('[TasksApp] Failed to load areas:', error);
            throw error;
          }
        },
        
        loadAllData: async () => {
          await Promise.all([
            get().loadTasks(),
            get().loadProjects(),
            get().loadAreas()
          ]);
        },

        // Filtering and searching
        setSearchQuery: (query: string) => {
          set((state) => {
            state.searchQuery = query;
          });
        },
        
        setFilters: (filters: Partial<TaskFilters>) => {
          set((state) => {
            state.filters = { ...state.filters, ...filters };
          });
        },
        
        setSort: (sort: TaskSort) => {
          set((state) => {
            state.sort = sort;
          });
        },
        
        clearFilters: () => {
          set((state) => {
            state.filters = {};
            state.searchQuery = '';
          });
        },
        
        // Selection methods
        selectTask: (taskId: string) => {
          set((state) => {
            if (!state.selectedTaskIds.includes(taskId)) {
              state.selectedTaskIds.push(taskId);
            }
          });
        },
        
        deselectTask: (taskId: string) => {
          set((state) => {
            state.selectedTaskIds = state.selectedTaskIds.filter(id => id !== taskId);
          });
        },
        
        selectAllTasks: () => {
          set((state) => {
            const visibleTasks = get().getFilteredTasks();
            state.selectedTaskIds = visibleTasks.map(t => t._id).filter(Boolean) as string[];
          });
        },
        
        clearSelection: () => {
          set((state) => {
            state.selectedTaskIds = [];
            state.selectedTaskId = null;
          });
        },
        
        setSelectedTaskId: (taskId: string | null) => {
          set((state) => {
            state.selectedTaskId = taskId;
          });
        },
        
        // Quick add
        setQuickAddText: (text: string) => {
          set((state) => {
            state.quickAddText = text;
          });
        },
        
        toggleQuickAdd: () => {
          set((state) => {
            state.showQuickAdd = !state.showQuickAdd;
            if (!state.showQuickAdd) {
              state.quickAddText = '';
            }
          });
        },
        
        submitQuickAdd: async () => {
          const { quickAddText, currentView, selectedProjectId, selectedAreaId } = get();
          
          if (!quickAddText.trim()) return;
          
          // Parse quick add text for smart defaults
          const taskData: Partial<Task['skeleton']> = {
            title: quickAddText.trim(),
            projectId: selectedProjectId,
            areaId: selectedAreaId,
            isInToday: currentView === 'today'
          };
          
          await get().createTask(taskData);
          
          set((state) => {
            state.quickAddText = '';
            state.showQuickAdd = false;
          });
        },
        
        // Editing
        setEditingTaskId: (taskId) => {
          set((state) => {
            state.editingTaskId = taskId;
          });
        },
        
        setExpandedTaskId: (taskId) => {
          set((state) => {
            state.expandedTaskId = taskId;
          });
        },
        
        createAndEditTask: async () => {
          const { currentView, selectedProjectId, selectedAreaId } = get();
          
          const taskData: Partial<Task['skeleton']> = {
            projectId: selectedProjectId,
            areaId: selectedAreaId,
            isInToday: currentView === 'today'
          };
          
          // Use draft task system instead of creating immediately
          get().createDraftTask(taskData);
        },
        
        // Display mode
        setDisplayMode: (mode: 'list' | 'board') => {
          set((state) => {
            state.displayMode = mode;
          });
        },
        
        // Computed getters
        getFilteredTasks: () => {
          const { tasks, searchQuery, filters, sort } = get();
          
          const filtered = tasks.filter(task => {
            // Search query filter
            if (searchQuery) {
              const query = searchQuery.toLowerCase();
              if (!task.name.toLowerCase().includes(query) &&
                  !task.skeleton.notes?.toLowerCase().includes(query) &&
                  !task.skeleton.tags.some(tag => tag.toLowerCase().includes(query))) {
                return false;
              }
            }
            
            // Completed filter
            if (filters.completed !== undefined && task.skeleton.completed !== filters.completed) {
              return false;
            }
            
            // Project filter
            if (filters.projectId !== undefined && task.skeleton.projectId !== filters.projectId) {
              return false;
            }
            
            // Area filter
            if (filters.areaId !== undefined && task.skeleton.areaId !== filters.areaId) {
              return false;
            }
            
            // Tags filter
            if (filters.tags && filters.tags.length > 0) {
              if (!filters.tags.some(tag => task.skeleton.tags.includes(tag))) {
                return false;
              }
            }
            
            // Priority filter
            if (filters.priority && filters.priority.length > 0) {
              if (!filters.priority.includes(task.skeleton.priority)) {
                return false;
              }
            }
            
            // Due date range filter
            if (filters.dueDateRange) {
              const taskDueDate = task.skeleton.dueDate;
              if (!taskDueDate) return !filters.dueDateRange.start && !filters.dueDateRange.end;
              
              if (filters.dueDateRange.start && taskDueDate < filters.dueDateRange.start) {
                return false;
              }
              if (filters.dueDateRange.end && taskDueDate > filters.dueDateRange.end) {
                return false;
              }
            }
            
            return true;
          });
          
          // Sort results
          filtered.sort((a, b) => {
            let aValue: any, bValue: any;
            
            switch (sort.field) {
              case 'title':
                aValue = a.name.toLowerCase();
                bValue = b.name.toLowerCase();
                break;
              case 'dueDate':
                aValue = a.skeleton.dueDate || '9999-12-31';
                bValue = b.skeleton.dueDate || '9999-12-31';
                break;
              case 'priority':
                const priorityOrder = { high: 3, medium: 2, low: 1 };
                aValue = priorityOrder[a.skeleton.priority];
                bValue = priorityOrder[b.skeleton.priority];
                break;
              case 'created_at':
                aValue = a.created_at;
                bValue = b.created_at;
                break;
              case 'order':
              default:
                aValue = a.skeleton.order;
                bValue = b.skeleton.order;
                break;
            }
            
            if (aValue < bValue) return sort.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sort.direction === 'asc' ? 1 : -1;
            return 0;
          });
          
          return filtered;
        },
        
        getTodayTasks: () => {
          const { tasks } = get();
          // Get today's date in local timezone (YYYY-MM-DD format)
          const now = new Date();
          const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
          
          return tasks
            .filter(task => 
              !task.skeleton.completed && 
              (task.skeleton.isInToday || task.skeleton.dueDate === today)
            )
            .sort((a, b) => (a.skeleton.order || 0) - (b.skeleton.order || 0));
        },
        
        getUpcomingTasks: () => {
          const { tasks } = get();
          // Get today's date in local timezone (YYYY-MM-DD format)
          const now = new Date();
          const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
          
          return tasks.filter(task => 
            !task.skeleton.completed && 
            task.skeleton.dueDate && 
            task.skeleton.dueDate > today
          ).sort((a, b) => (a.skeleton.dueDate || '').localeCompare(b.skeleton.dueDate || ''));
        },
        
        getInboxTasks: () => {
          const { tasks } = get();
          return tasks
            .filter(task => 
              !task.skeleton.completed &&
              !task.skeleton.projectId &&
              !task.skeleton.areaId &&
              !task.skeleton.isInToday
            )
            .sort((a, b) => (a.skeleton.order || 0) - (b.skeleton.order || 0));
        },
        
        getProjectTasks: (projectId: string) => {
          const { tasks } = get();
          return tasks
            .filter(task => task.skeleton.projectId === projectId)
            .sort((a, b) => (a.skeleton.order || 0) - (b.skeleton.order || 0));
        },
        
        getAreaTasks: (areaId: string) => {
          const { tasks } = get();
          return tasks
            .filter(task => task.skeleton.areaId === areaId)
            .sort((a, b) => (a.skeleton.order || 0) - (b.skeleton.order || 0));
        },
        
        getTasksByTag: (tag: string) => {
          const { tasks } = get();
          return tasks
            .filter(task => task.skeleton.tags.includes(tag))
            .sort((a, b) => (a.skeleton.order || 0) - (b.skeleton.order || 0));
        },
        
        getCompletedTasks: () => {
          const { tasks } = get();
          return tasks.filter(task => task.skeleton.completed)
            .sort((a, b) => (b.skeleton.completedAt || '').localeCompare(a.skeleton.completedAt || ''));
        },

        getTodayCompletedTasks: () => {
          const { tasks } = get();
          const now = new Date();
          const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
          return tasks
            .filter(task =>
              task.skeleton.completed &&
              (task.skeleton.isInToday || task.skeleton.dueDate === today)
            )
            .sort((a, b) => (b.skeleton.completedAt || '').localeCompare(a.skeleton.completedAt || ''));
        },

        loadDemoData: (areas: Area[], projects: Project[], tasks: Task[]) => {
          set((state) => {
            state.areas = areas;
            state.projects = projects;
            state.tasks = tasks;
            state.isDemo = true;
            state.isLoading = false;
            state.currentView = 'today';
          });
        }
      }))
    )
  );
};

// Store instance management
export const getTasksAppStore = (windowId: string, spaceId: string) => {
  const key = `${spaceId}:${windowId}`;
  if (!tasksAppStores.has(key)) {
    tasksAppStores.set(key, createTasksAppStore(windowId, spaceId));
  }
  return tasksAppStores.get(key)!;
};

// Store cleanup
export const cleanupTasksAppStore = (windowId: string, spaceId: string) => {
  const key = `${spaceId}:${windowId}`;
  const store = tasksAppStores.get(key);
  if (store) {
    store.getState().cleanup();
    tasksAppStores.delete(key);
  }
};

// Auto-sync to applicationState
export const setupTasksAppSync = (windowId: string, spaceId: string) => {
  const store = getTasksAppStore(windowId, spaceId);
  
  // Subscribe to routing state changes and sync to applicationState
  return store.subscribe(
    (state) => ({
      currentView: state.currentView,
      selectedProjectId: state.selectedProjectId,
      selectedAreaId: state.selectedAreaId,
      selectedTag: state.selectedTag,
      searchQuery: state.searchQuery,
      filters: state.filters,
      tasksCount: state.tasks.length,
      projectsCount: state.projects.length,
      areasCount: state.areas.length
    }),
    (appState) => {
      const { updateWindow, windows } = useSpaceStore.getState();
      
      if (!windows[windowId]) return;
      
      const prevAppState = (windows[windowId]?.applicationState as any) ?? {};
      updateWindow(windowId, {
        applicationState: {
          ...prevAppState,
          tasks: appState,
        }
      });
    },
    {
      // ✅ Use deep equality to prevent callback on unchanged data
      equalityFn: (a, b) => JSON.stringify(a) === JSON.stringify(b)
    }
  );
};
